import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, XCircle, AlertTriangle } from "lucide-react";

const AcceptableUsePolicyPage = () => (
  <section className="bg-cream-100 py-16">
    <div className="max-w-3xl mx-auto px-5 lg:px-10">
      <Link to="/" className="text-navy-700/70 hover:text-coral-300 inline-flex items-center gap-1 text-sm mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to home
      </Link>
      <p className="tracking-widest text-coral-300 text-xs font-semibold uppercase">Legal</p>
      <h1 className="font-serif text-navy-700 text-4xl md:text-5xl mt-2">
        Acceptable <span className="italic">Use Policy</span>
      </h1>
      <p className="text-sm text-navy-700/60 mt-2">
        Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
      </p>

      <div className="max-w-none mt-10 space-y-8 text-navy-700/85 leading-relaxed">
        <p className="text-lg">
          galeriecop is built for private event photo sharing — weddings, birthdays, graduations, corporate
          gatherings and the like. To keep it safe, private and useful for everyone, everyone who uses the
          Service — hosts and guests — agrees to these rules.
        </p>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-emerald-500" /> 1. Fair, private use
          </h2>
          <ul className="list-disc pl-6 mt-3 space-y-1.5">
            <li>Only upload content you have permission to share.</li>
            <li>Only invite guests you actually know or have hosted at your event.</li>
            <li>Treat other people&apos;s uploads with the same respect you&apos;d want for your own.</li>
            <li>Respect all applicable laws in your country and any country your guests are in.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic flex items-center gap-2">
            <XCircle className="w-6 h-6 text-red-500" /> 2. Prohibited content
          </h2>
          <p className="mb-2">You may not upload, host, share, or link to any of the following via galeriecop:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Sexual content involving anyone under 18 — this is a zero-tolerance ground for immediate account
              termination and mandatory reporting to law enforcement.</li>
            <li>Nudity or sexual content that isn&apos;t appropriate for a private event context.</li>
            <li>Content that harasses, threatens, bullies, defames, or dehumanises any individual or group.</li>
            <li>Content that incites violence, self-harm, or discrimination on the basis of race, ethnicity, national
              origin, religion, disability, age, sex, sexual orientation, or gender identity.</li>
            <li>Content that violates someone else&apos;s privacy, copyright, trademark, trade secret, or right of
              publicity.</li>
            <li>Malware, viruses, phishing links, or code intended to compromise devices.</li>
            <li>Personally identifiable information about people who have not consented to being in the gallery
              (e.g. photos of strangers, doxxing).</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic flex items-center gap-2">
            <XCircle className="w-6 h-6 text-red-500" /> 3. Prohibited use
          </h2>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Using the Service for commercial promotion, spam, mass marketing, or as a public content
              distribution platform. galeriecop is for private events, not audience broadcasting.</li>
            <li>Circumventing rate limits, tier limits, or trying to abuse promotional credits.</li>
            <li>Reverse-engineering, scraping, or accessing the Service other than through the intended interface.</li>
            <li>Using the Service to build a competing product, or to train machine-learning models on user content
              without our written consent.</li>
            <li>Interfering with the Service — DDoS attempts, credential-stuffing, or automated abuse.</li>
            <li>Impersonating another person, business, or brand.</li>
            <li>Any activity that violates applicable laws (including but not limited to export controls, sanctions
              regimes, or intellectual property law).</li>
          </ul>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-amber-500" /> 4. What happens if you break these rules
          </h2>
          <p>Depending on severity and repeat behaviour, we may:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1.5">
            <li>Remove offending content.</li>
            <li>Suspend or terminate the account, without refund.</li>
            <li>Preserve records and cooperate with law enforcement or regulatory investigations.</li>
            <li>Pursue civil claims where damages have been suffered.</li>
          </ul>
          <p className="mt-3">
            For content that constitutes an urgent safety risk (e.g. child sexual abuse material), we act
            immediately and report to the appropriate authorities. We do not warn or negotiate on these cases.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic">5. Reporting abuse</h2>
          <p>
            Seen something that doesn&apos;t belong on galeriecop? Please tell us —{" "}
            <a href="mailto:abuse@galeriecop.com" className="text-coral-400 hover:text-coral-500">abuse@galeriecop.com</a>.
            Include the event gallery link, a description of the issue, and (if applicable) which upload — we
            investigate every report and reply within 72 hours.
          </p>
          <p className="mt-3">
            Copyright infringement claims: send a takedown notice to the same address including the four elements
            required by your jurisdiction&apos;s notice-and-takedown regime (e.g. DMCA in the US). We respond
            promptly and forward valid notices to the uploader.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic">6. Related policies</h2>
          <p>
            This policy sits alongside our{" "}
            <Link to="/terms" className="text-coral-400 hover:text-coral-500 underline">Terms &amp; Conditions</Link>,{" "}
            <Link to="/privacy" className="text-coral-400 hover:text-coral-500 underline">Privacy Policy</Link>,{" "}
            <Link to="/cookies" className="text-coral-400 hover:text-coral-500 underline">Cookie Policy</Link>, and{" "}
            <Link to="/data-deletion" className="text-coral-400 hover:text-coral-500 underline">Data Deletion Policy</Link>.
            If any of them conflict, the Terms &amp; Conditions take precedence.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-navy-700 italic">7. Contact</h2>
          <p>
            Questions?{" "}
            <a href="mailto:hello@galeriecop.com" className="text-coral-400 hover:text-coral-500">hello@galeriecop.com</a>
            {" "}for general enquiries;{" "}
            <a href="mailto:abuse@galeriecop.com" className="text-coral-400 hover:text-coral-500">abuse@galeriecop.com</a>
            {" "}for reports.
          </p>
        </section>
      </div>
    </div>
  </section>
);

export default AcceptableUsePolicyPage;
